/**
 * Close a purchase order short (batch 8 T12c2, #3033): give up the units that will not be delivered.
 *
 * Closing short cancels, per line, what is still outstanding: ordered - received - held - cancelled. Held units are excluded (they
 * keep the order open until they are decided), rejected units are not subtracted. It is not a delivery: no stock changes and no
 * actual delivery date is set. The user gives a reason, chooses explicitly between every line with something outstanding and a
 * selection, and confirms a summary. A replay of a lost answer shows the FIRST outcome.
 *
 * Standalone: it takes the order lines as props and enforces the owner/target boundary itself.
 */

import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  Checkbox,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { ResponsiveDialog } from '@/components/common/ResponsiveDialog';
import { useNotify } from '@/hooks/useNotify';
import { useReceivingOperations, type SubmitResult } from '@/hooks/usePurchaseReceipts';
import { emptyCloseShortDraft, evaluateCloseShort, remainingOf, type CloseShortDraft } from './receivingArithmetic';
import { slotOf } from './receivingJournal';
import { blockedMessage, JournalNotice, PendingOperationAlert, ReceivingBoundary, ReceivingErrorAlert } from './receivingParts';
import { CLOSE_SHORT_REASON_MAX, type CloseShortResult, type ReceivingLine } from './receivingTypes';

export interface CloseShortDialogProps {
  open: boolean;
  onClose: () => void;
  purchaseOrderId: string;
  orderNumber?: string | undefined;
  lines: readonly ReceivingLine[];
  onClosed?: ((result: CloseShortResult) => void) | undefined;
}

export function CloseShortDialog(props: CloseShortDialogProps) {
  return (
    <ReceivingBoundary target={props.purchaseOrderId} dialog={{ open: props.open, onClose: props.onClose }}>
      <CloseShortForm {...props} />
    </ReceivingBoundary>
  );
}

type Stage = 'edit' | 'review' | 'done';

const lineLabel = (line: ReceivingLine): string => `${line.partNumber} ${line.name}`.trim();

function CloseShortForm({ onClose, purchaseOrderId, orderNumber, lines, onClosed }: CloseShortDialogProps) {
  const { t } = useTranslation('procurementReceiving');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const ops = useReceivingOperations(purchaseOrderId);
  const [draft, setDraft] = useState<CloseShortDraft>(emptyCloseShortDraft());
  const [stage, setStage] = useState<Stage>('edit');
  const [reviewed, setReviewed] = useState<string | null>(null);
  const [attempted, setAttempted] = useState(false);
  const [result, setResult] = useState<CloseShortResult | null>(null);
  const [refusal, setRefusal] = useState<unknown>(null);
  const [blocked, setBlocked] = useState<string | null>(null);
  const sending = useRef(false);

  // A line with nothing outstanding cannot be named: the server refuses the whole request for it (`nothing_to_cancel`). A selection
  // that holds one (the order changed under the draft) loses it in this render instead of being sent.
  const effective = useMemo<CloseShortDraft>(
    () => ({ ...draft, selected: draft.selected.filter((id) => { const line = lines.find((candidate) => candidate.id === id); return line !== undefined && remainingOf(line) !== 0; }) }),
    [draft, lines],
  );
  const { problems, body } = evaluateCloseShort(effective);
  // What the review showed: the request AND the outstanding quantity of each line it concerns, so a refetch that changes one drops it.
  const reviewedLines =
    draft.scope === 'selected' ? lines.filter((line) => effective.selected.includes(line.id)) : lines.filter((line) => (remainingOf(line) ?? 0) > 0);
  const signature = body ? JSON.stringify({ body, facts: (draft.scope === 'selected' ? reviewedLines : lines).map((line) => [line.id, remainingOf(line)]) }) : null;
  const reviewing = stage === 'review' && signature !== null && signature === reviewed;
  const shown: Stage = stage === 'review' && !reviewing ? 'edit' : stage;

  const slot = slotOf('close_short', purchaseOrderId);
  const held = ops.entries.find((entry) => entry.slot === slot) ?? null;
  const pending = ops.isPending(slot);
  const lineById = useMemo(() => new Map(lines.map((line) => [line.id, line])), [lines]);

  const update = (patch: Partial<CloseShortDraft>) => {
    setBlocked(null);
    setRefusal(null);
    setDraft((current) => ({ ...current, ...patch }));
  };
  const toggle = (lineId: string) =>
    update({ selected: draft.selected.includes(lineId) ? draft.selected.filter((id) => id !== lineId) : [...draft.selected, lineId] });

  const handle = (outcome: SubmitResult) => {
    if (outcome.status === 'success') {
      const closed = outcome.result as CloseShortResult;
      setResult(closed);
      setStage('done');
      setRefusal(null);
      notify.success(t('closeShort.notifyTitle'), t('closeShort.notifyBody'));
      onClosed?.(closed);
    } else if (outcome.status === 'refused') {
      setRefusal(outcome.error);
      setStage('edit');
    } else if (outcome.status === 'blocked') {
      setBlocked(blockedMessage(outcome.reason, t as never));
      setStage('edit');
    } else if (outcome.status === 'uncertain') {
      setStage('edit');
    }
  };

  const review = () => {
    setAttempted(true);
    if (signature === null) return;
    setReviewed(signature);
    setStage('review');
  };

  const confirm = async () => {
    if (!reviewing || body === null || sending.current) return;
    sending.current = true;
    try {
      handle(await ops.closeShort(body));
    } finally {
      sending.current = false;
    }
  };

  const retry = async () => {
    if (held === null || sending.current) return;
    sending.current = true;
    try {
      handle(await ops.retry(held));
    } finally {
      sending.current = false;
    }
  };

  const title = orderNumber ? t('closeShort.titleFor', { number: orderNumber }) : t('closeShort.title');
  const storageOk = ops.journalStatus === 'ok';

  return (
    <ResponsiveDialog open onClose={onClose} fullWidth maxWidth="sm" mobileTitle={title} aria-labelledby="close-short-title">
      <DialogTitle id="close-short-title">{title}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <JournalNotice status={ops.journalStatus} onDiscardDamaged={ops.discardDamaged} onRecheck={ops.recheckStorage} />
          {!ops.canReceive && <Alert severity="warning">{t('errors.noPermission')}</Alert>}
          <Alert severity="info">
            <strong>{t('closeShort.explainTitle')}</strong> {t('closeShort.explainFormula')} {t('closeShort.explainHeld')} {t('closeShort.explainNotDelivery')}
          </Alert>

          {shown === 'done' && result !== null && (
            <Stack spacing={1}>
              <Alert severity="success">{t('closeShort.done.title')}</Alert>
              {result.replayed && <Alert severity="info">{t('closeShort.done.replayed')}</Alert>}
              {result.lines.map((line) => {
                const source = lineById.get(line.lineId);
                return (
                  <Typography key={line.lineId} variant="body2">
                    {t('closeShort.done.line', { part: source ? lineLabel(source) : t('history.unknownLine'), cancelled: line.cancelled })}
                    {line.heldUnresolved > 0 && ` ${t('closeShort.done.heldLeft', { count: line.heldUnresolved })}`}
                  </Typography>
                );
              })}
              <Typography variant="body2">{result.fullyReceived ? t('receive.done.fullyReceived') : t('receive.done.notFullyReceived')}</Typography>
            </Stack>
          )}

          {shown !== 'done' && held !== null && (
            <PendingOperationAlert
              entry={held}
              error={ops.errorOf(slot)}
              busy={pending}
              journalStatus={ops.journalStatus}
              onRetry={() => void retry()}
              onDiscard={() => ops.discard(held)}
            />
          )}

          {shown === 'edit' && held === null && (
            <Stack spacing={2}>
              <TextField
                size="small"
                required
                multiline
                minRows={2}
                label={t('closeShort.reason')}
                value={draft.reason}
                onChange={(event) => update({ reason: event.target.value })}
                error={attempted && (problems.includes('reasonRequired') || problems.includes('reasonTooLong'))}
                helperText={attempted && problems.includes('reasonRequired') ? t('closeShort.problems.reasonRequired') : t('closeShort.reasonHint')}
                slotProps={{ htmlInput: { maxLength: CLOSE_SHORT_REASON_MAX } }}
              />
              <FormControl error={attempted && problems.includes('scopeRequired')}>
                <FormLabel id="close-short-scope">{t('closeShort.scope.label')}</FormLabel>
                <RadioGroup
                  aria-labelledby="close-short-scope"
                  value={draft.scope}
                  onChange={(event) => update({ scope: event.target.value as CloseShortDraft['scope'] })}
                >
                  <FormControlLabel value="all" control={<Radio />} label={t('closeShort.scope.all')} />
                  <FormControlLabel value="selected" control={<Radio />} label={t('closeShort.scope.selected')} />
                </RadioGroup>
              </FormControl>
              {draft.scope === 'selected' && (
                <FormControl error={attempted && problems.includes('noneSelected')}>
                  <FormLabel id="close-short-lines">{t('closeShort.scope.lines')}</FormLabel>
                  <FormGroup aria-labelledby="close-short-lines">
                    {lines.map((line) => {
                      const remaining = remainingOf(line);
                      return (
                        <FormControlLabel
                          key={line.id}
                          control={<Checkbox checked={effective.selected.includes(line.id)} disabled={remaining === 0} onChange={() => toggle(line.id)} />}
                          label={
                            remaining === null
                              ? lineLabel(line)
                              : `${lineLabel(line)} — ${remaining === 0 ? t('closeShort.nothingOutstanding') : t('closeShort.expected', { count: remaining })}`
                          }
                        />
                      );
                    })}
                  </FormGroup>
                </FormControl>
              )}
              {attempted &&
                problems
                  .filter((problem) => problem !== 'reasonRequired')
                  .map((problem) => (
                    <Typography key={problem} variant="caption" color="error" role="alert">
                      {t(`closeShort.problems.${problem}`)}
                    </Typography>
                  ))}
            </Stack>
          )}

          {shown === 'review' && held === null && body !== null && (
            <Stack spacing={1}>
              <Alert severity="warning">
                <strong>{t('closeShort.summary.title')}</strong> {t(draft.scope === 'all' ? 'closeShort.summary.all' : 'closeShort.summary.selected', { count: body.lineIds?.length ?? 0 })}
              </Alert>
              {reviewedLines.map((line) => {
                  const remaining = remainingOf(line);
                  return (
                    <Typography key={line.id} variant="body2">
                      {remaining === null ? lineLabel(line) : `${lineLabel(line)} — ${t('closeShort.expected', { count: remaining })}`}
                    </Typography>
                  );
                })}
              <Typography variant="body2">{t('closeShort.summary.reason', { reason: body.reason })}</Typography>
              <Typography variant="body2" color="text.secondary">
                {t('closeShort.summary.confirmHint')}
              </Typography>
            </Stack>
          )}

          <ReceivingErrorAlert error={refusal} />
          {blocked !== null && <Alert severity="error">{blocked}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        {shown === 'done' ? (
          <Button onClick={onClose} variant="contained">
            {tc('close')}
          </Button>
        ) : (
          <>
            <Button onClick={onClose}>{tc('cancel')}</Button>
            {held === null && shown === 'review' && <Button onClick={() => setStage('edit')}>{tc('back')}</Button>}
            {held === null && shown === 'edit' && (
              <Button variant="contained" onClick={review} disabled={!ops.canReceive || !storageOk}>
                {t('closeShort.review')}
              </Button>
            )}
            {held === null && shown === 'review' && (
              <Button variant="contained" color="warning" onClick={() => void confirm()} disabled={pending || !ops.canReceive || !storageOk}>
                {t('closeShort.confirm')}
              </Button>
            )}
          </>
        )}
      </DialogActions>
    </ResponsiveDialog>
  );
}
